import React, { useState, useCallback } from "react";
import LabBookingGantt from "../../Common/GanttChart/index";
import BookingConfirmationModal from "../../Common/Modal/BookingConfirmationModal";

// ─── Local ISO helper (same as modal, avoids UTC shift) ───────────────────────
function toLocalISOString(date) {
  if (!date || isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
}

export default function LabBookingStep3Schedule({
  form,
  errors,
  onChange,
  onConfirm,
  onScheduleChange,
  isSaving,
  saveError,
  selectedLab,
}) {
  const [ganttSelection, setGanttSelection] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // ── Step 1: Gantt fires onBookingSelect ─────────────────────────────────────
  // Store the raw gantt result and open the modal.
  // Do NOT write to form yet — the user may edit dates in the modal.
  const handleGanttBooking = useCallback((booking) => {
    if (!booking?.startDateTime || !booking?.endDateTime) return;
    const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
    if (booking.startDateTime < todayMidnight) return;

    const startISO =
      booking.startDateTimeStr || toLocalISOString(booking.startDateTime);
    const endISO =
      booking.endDateTimeStr || toLocalISOString(booking.endDateTime);
    const hours =
      booking.durationHours ||
      (booking.endDateTime - booking.startDateTime) / 3600000;

    // Just store selection for the modal — form NOT updated here
    setGanttSelection({
      resource: booking.resource,
      start: startISO, // local ISO string, no Z
      end: endISO,
      hours: Math.round(hours * 10) / 10,
      bookableDays: booking.bookableDays || null,
      startDateTime: booking.startDateTime,
      endDateTime: booking.endDateTime,
      hasOverlap: booking.hasOverlap,
    });

    setShowConfirmationModal(true);
  }, []);

  const handleModalConfirm = useCallback(
    (modalData) => {
      const startISO = modalData.startDateTimeStr;
      const endISO = modalData.endDateTimeStr;
      const hours = modalData.durationHours;

      console.log("[Step3] Writing to form → start:", startISO, "end:", endISO);

       
      onScheduleChange({
        Planned_Start_Date_Hrs: startISO,
        Planned_Hrs: Math.round(hours * 10) / 10,
      });
    

      // ── Write ALL booking fields to form at once ──────────────────────────
      onChange("Planned_Start_Date_Hrs", startISO);
      onChange("Planned_End_Date_Hrs", endISO);
      onChange("Planned_Hrs", Math.round(hours * 10) / 10);
      onChange("Project_ID", modalData.projectId || "");
      onChange("Project_Test_Det_ID", modalData.testTypeId || "");
      onChange("Booking_Remarks", modalData.remarks || "");

      setShowConfirmationModal(false);

      // ✅ Pass the data directly so onConfirm doesn't depend on form state being flushed
      if (onConfirm)
        onConfirm({
          Planned_Start_Date_Hrs: startISO,
          Planned_End_Date_Hrs: endISO,
          Planned_Hrs: Math.round(hours * 10) / 10,
          Project_ID: modalData.projectId || "",
          Project_Test_Det_ID: modalData.testTypeId || "",
          Booking_Remarks: modalData.remarks || "",
        });
    },
    [onChange, onConfirm,onScheduleChange],
  );

  return (
    <div className="space-y-6">
      {/* Gantt Chart Scheduler */}
      <div className="bg-white p-6 rounded-2xl shadow-[0px_20px_40px_rgba(24,28,29,0.06)] border border-white/40">
        <h3 className="text-base font-bold flex items-center gap-2 mb-5">
          <span className="material-symbols-outlined text-[#08757c] text-[20px]">
            event_available
          </span>
          Select Booking Schedule
        </h3>

        {selectedLab && (
          <div className="mb-4 p-3 bg-[#08757c]/10 rounded-lg">
            <div className="flex items-center gap-2 text-sm">
              <span className="material-symbols-outlined text-[#08757c] text-[18px]">
                biotech
              </span>
              <span className="text-[#3e494a]">Selected Lab:</span>
              <span className="font-bold text-[#08757c]">
                {selectedLab.facilityName}
              </span>
            </div>
          </div>
        )}

        <LabBookingGantt
          onBookingSelect={handleGanttBooking}
          defaultResource={selectedLab?.facilityName}
          selectedLab={selectedLab}
           onPreviewChange={(preview) => {
    // ✅ Updates sidebar live without opening modal
    if (onScheduleChange) {
      onScheduleChange({
        Planned_Start_Date_Hrs: preview.startDateTimeStr,
        Planned_Hrs: preview.durationHours,
      });
    }
  }}
        />

        <div className="mt-4 p-3 bg-[#f7fafa] rounded-lg">
          <div className="flex items-center gap-2 text-xs text-[#3e494a]">
            <span className="material-symbols-outlined text-[14px]">info</span>
            <span>
              Select your booking schedule using the Gantt chart above
            </span>
          </div>
        </div>
      </div>

      {/* Error display */}
      {saveError && (
        <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
          <span className="material-symbols-outlined text-[20px]">error</span>
          {saveError}
        </div>
      )}

      {errors?.start && (
        <div className="bg-red-50 text-[#ba1a1a] rounded-xl px-4 py-3 text-sm flex items-center gap-3">
          <span className="material-symbols-outlined text-[20px]">error</span>
          {errors.start}
        </div>
      )}

      {/* Booking Confirmation Modal */}
      <BookingConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleModalConfirm}
        bookingSummary={{
          lab: selectedLab?.facilityName || ganttSelection?.resource,
          start: ganttSelection?.start,
          end: ganttSelection?.end,
          hours: ganttSelection?.hours,
          bookableDays: ganttSelection?.bookableDays,
          hasOverlap: ganttSelection?.hasOverlap,
        }}
        
      />
    </div>
  );
}
